import React, { useEffect, useRef, useState } from 'react';
import styles from './CodeLock.module.scss';
import { useTickSound } from '../../hooks/UseTickSound';

type Props = {
  value: number; // 0..9
  onChange: (digit: number) => void;
  height: number; // высота барабана (видимых 3 строки)
  adPhase?: boolean; // true → доснять жест и зафиксировать
  disabled?: boolean;
};

export default function DigitWheel({
  value,
  onChange,
  height,
  adPhase = false,
  disabled = false,
}: Props) {
  const [currentValue, setCurrentValue] = useState(value);

  const dragOffsetRef = useRef(0);
  const animOffsetRef = useRef(0);
  const animFrame = useRef<number | null>(null);

  const startY = useRef(0);
  const isDragging = useRef(false);
  const lastStepRef = useRef(0);

  // 🔊 звук тика
  const { ready, init, playOnce } = useTickSound({
    src: '/tickCut.wav',
    volume: 1,
  });
  const initPromiseRef = useRef<Promise<void> | null>(null);
  const pendingTicksRef = useRef(0);

  const itemHeight = height / 3;
  const baseOffset = itemHeight;
  const norm = (n: number) => ((n % 10) + 10) % 10;

  const [, setTick] = useState(0);
  const forceRender = () => setTick((x) => x + 1);

  // Пропсы — источник истины всегда
  useEffect(() => {
    setCurrentValue(value);
    dragOffsetRef.current = 0;
    animOffsetRef.current = 0;
    lastStepRef.current = 0;
    forceRender();
  }, [value]);

  const totalOffset = dragOffsetRef.current + animOffsetRef.current;

  const visibleDigits = (() => {
    const arr: number[] = [];
    for (let i = -15; i <= 15; i++) arr.push(norm(currentValue + i));
    return arr;
  })();

  const centerIndex = Math.floor(visibleDigits.length / 2);
  const positionForIndex = (index: number) =>
    (index - centerIndex) * itemHeight + baseOffset - totalOffset * itemHeight;

  const drainPendingTicks = () => {
    const n = pendingTicksRef.current;
    if (n > 0) {
      for (let i = 0; i < n; i++) playOnce();
      pendingTicksRef.current = 0;
    }
  };

  const maybeTickOnBoundary = () => {
    const continuous = dragOffsetRef.current + animOffsetRef.current;
    const step =
      continuous < 0 ? Math.ceil(continuous) : Math.floor(continuous);
    if (step === lastStepRef.current) return;

    const passed = Math.abs(step - lastStepRef.current);
    if (ready) {
      for (let i = 0; i < passed; i++) playOnce();
    } else {
      pendingTicksRef.current += passed;
    }
    lastStepRef.current = step;
  };

  const animateToSnap = (targetSteps: number) => {
    const start = dragOffsetRef.current + animOffsetRef.current;
    const end = Math.round(targetSteps);
    const distance = end - start;

    if (distance === 0) {
      const next = norm(currentValue + end);
      setCurrentValue(next);
      onChange(next);
      dragOffsetRef.current = 0;
      animOffsetRef.current = 0;
      lastStepRef.current = 0;
      forceRender();
      return;
    }

    const duration = Math.min(600, Math.max(150, Math.abs(distance) * 70));
    const t0 = performance.now();
    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

    if (animFrame.current != null) cancelAnimationFrame(animFrame.current);
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / duration);
      const e = easeOutCubic(p);
      animOffsetRef.current = start + distance * e - dragOffsetRef.current;
      maybeTickOnBoundary();
      forceRender();

      if (p < 1) {
        animFrame.current = requestAnimationFrame(tick);
      } else {
        const snaps = Math.round(end);
        const next = norm(currentValue + snaps);
        setCurrentValue(next);
        onChange(next);
        dragOffsetRef.current = 0;
        animOffsetRef.current = 0;
        lastStepRef.current = 0;
        if (ready) drainPendingTicks();
        forceRender();
        animFrame.current = null;
      }
    };
    animFrame.current = requestAnimationFrame(tick);
  };

  // завершение жеста (универсальное)
  const endDrag = () => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const snaps = Math.round(dragOffsetRef.current);
    animateToSnap(snaps);
  };

  // Pointer события (стабильнее в WebView)
  const handlePointerDown = (e: React.PointerEvent) => {
    if (animFrame.current != null || disabled) return;
    (e.target as Element).setPointerCapture?.(e.pointerId);
    isDragging.current = true;
    startY.current = e.clientY;
    dragOffsetRef.current = 0;
    animOffsetRef.current = 0;
    lastStepRef.current = 0;

    if (!ready && !initPromiseRef.current) {
      initPromiseRef.current = init()
        .then(() => drainPendingTicks())
        .finally(() => (initPromiseRef.current = null));
    }
  };
  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging.current || disabled) return;
    const deltaY = startY.current - e.clientY;
    dragOffsetRef.current = deltaY / itemHeight;
    maybeTickOnBoundary();
    forceRender();
  };
  const handlePointerUp = () => endDrag();
  const handlePointerCancel = () => endDrag();

  // Touch (на случай, если pointer-events отключены)
  const handleTouchStart = (e: React.TouchEvent) => {
    if (animFrame.current != null || disabled) return;
    e.preventDefault();
    isDragging.current = true;
    startY.current = e.touches[0].clientY;
    dragOffsetRef.current = 0;
    animOffsetRef.current = 0;
    lastStepRef.current = 0;

    if (!ready && !initPromiseRef.current) {
      initPromiseRef.current = init()
        .then(() => drainPendingTicks())
        .finally(() => (initPromiseRef.current = null));
    }
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging.current || disabled) return;
    e.preventDefault();
    e.stopPropagation();
    const y = e.touches[0].clientY;
    const deltaY = startY.current - y;
    dragOffsetRef.current = deltaY / itemHeight;
    maybeTickOnBoundary();
    forceRender();
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    e.preventDefault();
    if (!isDragging.current || disabled) return;
    endDrag();
  };
  const handleTouchCancel = (e: React.TouchEvent) => {
    e.preventDefault();
    endDrag();
  };

  // Принудительный сброс при рекламе/запросе/disabled
  useEffect(() => {
    if (adPhase || disabled) endDrag();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adPhase, disabled]);

  // Если вкладка ушла в бэкграунд — тоже завершаем жест
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState !== 'visible') endDrag();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      if (animFrame.current != null) cancelAnimationFrame(animFrame.current);
    };
  }, []);

  return (
    <div className={styles.drum} style={{ height }}>
      <div
        className={styles.digitContainer}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchCancel}
        style={{ height }}
      >
        {visibleDigits.map((digit, index) => {
          const rel =
            index - Math.floor(visibleDigits.length / 2) - totalOffset;
          const y = positionForIndex(index);
          const isActive = Math.abs(rel) < 0.5;
          const clamped = Math.max(-2, Math.min(2, rel));
          const angle = clamped * 12;
          const z = Math.abs(clamped) * -8;

          return (
            <div
              key={index}
              className={styles.digitItem}
              style={{
                transform: `translate3d(0, ${y}px, ${z}px) rotateX(${angle}deg)`,
              }}
            >
              <div
                className={`${styles.digitInner} ${isActive ? styles.innerActive : styles.innerInactive}`}
              >
                {digit}
              </div>
            </div>
          );
        })}
      </div>

      <div className={styles.window} style={{ height: itemHeight }} />
    </div>
  );
}
